/**
 * Company, contact and legal facts — declared once, supplied by environment.
 *
 * Every legal page in this app reads its facts from here, so there is one place
 * to correct rather than four documents to keep in step.
 *
 * The values come from `VITE_COMPANY_*` variables in `apps/marketing-web/.env`,
 * which is committed: all of it is rendered on public pages, so none of it is
 * secret. **These are build-time values.** Vite compiles them into the bundle,
 * so changing one means editing `.env` and running `npm run build` again — the
 * deployed site does not read the file and will not pick up an edit on its own.
 *
 * An unset variable becomes `null` rather than an empty string. A missing fact
 * then renders in place as a marked gap and is listed in a banner at the top of
 * each legal document, which is the point: a made-up registered address inside
 * a Terms document is worse than a visible gap, because only one of the two
 * gets noticed before a buyer relies on it.
 */

/** Trim, and treat blank as absent — a bare `VITE_X=` line means "not supplied". */
function fact(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export interface CompanyFacts {
  /** Product and trading name, as customers know it. */
  product: string;
  /** Bare domain, for prose that reads better without a scheme. */
  domain: string;
  siteUrl: string;
  /** Registered company name that sells the subscription. */
  legalName: string | null;
  /** Company registration number, if the entity has one. Rendered only when set. */
  registrationNumber: string | null;
  /** Registered address, single line. */
  registeredAddress: string | null;
  /**
   * Governing law, phrased to drop into "the laws of X" — so the value is the
   * *place*, not the phrase. "Sri Lanka", not "Sri Lankan law", or the Terms
   * read "the laws of Sri Lankan law". The Terms give the courts of the same
   * place exclusive jurisdiction, so this is one fact to supply rather than two.
   */
  governingLaw: string | null;
  /** Buyer support telephone number, shown beside the support email. */
  supportPhone: string | null;
  email: {
    support: string;
    sales: string;
    security: string;
  };
  builtBy: { name: string; url: string };
}

export const COMPANY: CompanyFacts = {
  product: 'WaveQA',
  domain: 'waveqa-agent.com',
  siteUrl: 'https://waveqa-agent.com',

  legalName: fact(import.meta.env.VITE_COMPANY_LEGAL_NAME),
  registrationNumber: fact(import.meta.env.VITE_COMPANY_REGISTRATION_NUMBER),
  registeredAddress: fact(import.meta.env.VITE_COMPANY_REGISTERED_ADDRESS),
  governingLaw: fact(import.meta.env.VITE_COMPANY_GOVERNING_LAW),
  supportPhone: fact(import.meta.env.VITE_COMPANY_SUPPORT_PHONE),

  email: {
    support: 'support@waveqa-agent.com',
    sales: 'hello@waveqa-agent.com',
    security: 'security@waveqa-agent.com',
  },
  builtBy: { name: 'Waves Are Calling', url: 'https://wavesarecalling.com' },
};

/**
 * How payment is taken, and who the buyer's contract is with.
 *
 * WaveQA sells **directly**. Card payments are processed by a bank payment
 * gateway, which is a processor rather than a reseller — so unlike a
 * Merchant-of-Record arrangement, the contract for the transaction is between
 * the buyer and WaveQA, and invoices, billing questions and refunds are handled
 * by WaveQA rather than by the payment provider.
 *
 * That distinction is what the buyer-facing copy has to reflect: nothing may
 * suggest a third party is answerable for an order, because none is.
 */
export const PAYMENTS = {
  /** Gateway operator, as named to buyers. */
  gatewayName: fact(import.meta.env.VITE_PAYMENTS_GATEWAY_NAME),
  /** Card schemes accepted. */
  cardSchemes: 'Visa and Mastercard',
  /** Currency prices are charged in. */
  currency: 'USD',
} as const;

/**
 * One sentence on who sells and who merely processes.
 *
 * Carried in the footer, on Pricing and on Support — the three places a buyer
 * looks before paying. Assembled here rather than written out three times, so
 * a change to the arrangement cannot leave one page describing the old one.
 *
 * It says "processor, not reseller" explicitly because the previous
 * arrangement was the opposite, and a buyer who has seen a Merchant-of-Record
 * notice elsewhere will otherwise assume the gateway is answerable for their
 * order. It is not; we are.
 */
export const SELLER_NOTICE: string = [
  `${COMPANY.product} is sold directly by ${COMPANY.legalName ?? COMPANY.product}.`,
  PAYMENTS.gatewayName
    ? `Card payments are processed by ${PAYMENTS.gatewayName}, acting as a payment processor rather than as a reseller.`
    : 'Card payments are processed by our payment gateway, acting as a payment processor rather than as a reseller.',
  'Invoices, billing questions and refunds are handled by us.',
].join(' ');

/**
 * Third parties that process customer data on our behalf.
 *
 * Published because a privacy policy is expected to name them, and because
 * enterprise buyers ask for the list during review. Supplied by environment so
 * the list can be corrected without a code change; an unnamed provider renders
 * as a gap rather than as a guess a buyer might rely on.
 */
export interface Subprocessor {
  purpose: string;
  /** Provider name, or `null` while the choice is open. */
  name: string | null;
  /** Their own privacy notice, where the provider is settled. */
  url: string | null;
  /**
   * One sentence qualifying the row, for an arrangement a name alone states
   * wrongly. Rendered under the purpose; omitted where the name says it all.
   */
  note?: string;
}

export const SUBPROCESSORS: Subprocessor[] = [
  {
    purpose: 'Card payment processing',
    name: PAYMENTS.gatewayName,
    url: fact(import.meta.env.VITE_PAYMENTS_GATEWAY_PRIVACY_URL),
  },
  {
    purpose: 'Application and database hosting',
    name: fact(import.meta.env.VITE_SUBPROCESSOR_HOSTING),
    url: fact(import.meta.env.VITE_SUBPROCESSOR_HOSTING_URL),
  },
  {
    purpose: 'Transactional email delivery',
    name: fact(import.meta.env.VITE_SUBPROCESSOR_EMAIL),
    url: fact(import.meta.env.VITE_SUBPROCESSOR_EMAIL_URL),
  },
  {
    // Not our subprocessor at all, and listed to say so. The agent calls
    // whichever provider the customer configures, under the customer's own API
    // key, which is held in their OS keystore and never reaches our servers —
    // so no model vendor processes their data on our behalf, and naming one
    // here would invent a data flow. See `wave_agent.credentials`.
    purpose: 'Language-model inference used to reason about test results',
    name: 'You',
    url: null,
    note: 'The provider and model are yours to choose. The agent calls them under your own API key, held in your keystore — it never reaches us, and no model vendor processes your data on our behalf.',
  },
];

/** A fact the legal documents cite that nobody has supplied yet. */
export interface UnresolvedFact {
  label: string;
  note: string;
}

const REQUIRED_BEFORE_LAUNCH: (UnresolvedFact & { value: string | null })[] = [
  {
    value: COMPANY.legalName,
    label: 'Registered legal name',
    note: 'The selling company’s name — or a sole proprietor’s legal name — belongs inside the Terms and Conditions.',
  },
  {
    value: COMPANY.registeredAddress,
    label: 'Registered address',
    note: 'Buyers, and several consumer regimes, expect a postal address for the seller.',
  },
  {
    value: COMPANY.governingLaw,
    label: 'Governing law and jurisdiction',
    note: 'A contract with no stated governing law leaves every dispute arguing about which one applies.',
  },
  {
    value: COMPANY.supportPhone,
    label: 'Buyer support telephone number',
    note: 'A support telephone number belongs alongside the support email address.',
  },
  {
    value: PAYMENTS.gatewayName,
    label: 'Payment gateway operator',
    note: 'The privacy policy must name whoever processes card details, and buyers are entitled to know who that is.',
  },
];

/** Empty once every fact above has been filled in. Drives the document banners. */
export const UNRESOLVED_FACTS: UnresolvedFact[] = REQUIRED_BEFORE_LAUNCH.filter(
  (entry) => entry.value === null,
).map(({ label, note }) => ({ label, note }));
